import { useState } from 'react'
import {
  type GestureResponderEvent,
  Pressable as RNPressable,
  type PressableProps,
} from 'react-native'
import { productTelemetryService } from '@/lib/services/product-telemetry.service'

type TrackedNativePressableProps = PressableProps & {
  analyticsId?: string
  analyticsLabel?: string
}

type TelemetryIdentity = {
  elementId: string | null
  elementIdQuality: 'stable' | 'stable_auto'
  elementLabel: string | null
}

function pressableIdentityForTelemetry({
  accessibilityLabel,
  analyticsId,
  analyticsLabel,
  testID,
}: {
  accessibilityLabel?: string
  analyticsId?: string
  analyticsLabel?: string
  testID?: string
}): TelemetryIdentity {
  const elementLabel = analyticsLabel?.trim() || accessibilityLabel?.trim() || null
  if (analyticsId?.trim()) {
    return { elementId: analyticsId.trim(), elementIdQuality: 'stable', elementLabel }
  }
  if (testID?.trim()) {
    return { elementId: testID.trim(), elementIdQuality: 'stable', elementLabel }
  }
  if (accessibilityLabel?.trim()) {
    return { elementId: null, elementIdQuality: 'stable_auto', elementLabel }
  }
  return { elementId: null, elementIdQuality: 'stable_auto', elementLabel: null }
}

/**
 * Resolve `style` here instead of handing the function to React Native.
 *
 * `PressableProps['style']` may be `(state) => style`, and RN's own Pressable
 * supports that. This app's does not, because NativeWind 4 applies its style
 * interop to the core RN components — `Pressable` among them — and that interop
 * normalises `style` as an object or array. A function is neither, so it is
 * dropped, and the element renders with NO STYLE AT ALL.
 *
 * Not "loses its press feedback": no border, no background, no radius, no
 * `flexDirection: 'row'`. The DatePicker trigger on the signup form rendered as
 * a bare calendar glyph with the placeholder underneath it, hard against the
 * screen edge, on every Android build the product has ever shipped.
 *
 * It never showed up because react-native-web resolves the function, so the
 * field is pixel-correct in a browser — which is where it kept being measured.
 * `components/ui/Pressable` already resolves it by hand for the RNGH path
 * (Pressable.tsx:167); this is the same resolution for the native RN path, so
 * the two wrappers finally behave the same.
 */
export function TrackedNativePressable({
  accessibilityLabel,
  accessibilityRole,
  analyticsId,
  analyticsLabel,
  onPress,
  onPressIn,
  onPressOut,
  style,
  testID,
  ...props
}: TrackedNativePressableProps) {
  // `pressed` has to be tracked here because resolving the style means we can
  // no longer read it from Pressable's own render callback.
  const [pressed, setPressed] = useState(false)

  /**
   * The state handed to a caller's `style` function.
   *
   * Declared as a variable rather than written inline, and that is load-bearing.
   * `PressableStateCallbackType` is `{ pressed }` in react-native 0.81 and
   * `{ pressed, hovered, focused }` in react-native-web, and this file is
   * type-checked against both depending on which resolution wins — a literal
   * argument fails one way or the other every time (CI said `hovered` does not
   * exist; this machine said it was required and missing). TypeScript only
   * applies excess-property checks to object literals passed directly, so a
   * variable carrying the superset satisfies both shapes with no cast.
   *
   * Nothing hovers or focuses on the native path; the fields exist so the call
   * type-checks wherever this file is compiled.
   */
  const styleState = { pressed, hovered: false, focused: false }

  const handlePressIn = (event: GestureResponderEvent) => {
    setPressed(true)
    onPressIn?.(event)
  }

  const handlePressOut = (event: GestureResponderEvent) => {
    setPressed(false)
    onPressOut?.(event)
  }

  const handlePress = (event: GestureResponderEvent) => {
    const identity = pressableIdentityForTelemetry({
      accessibilityLabel,
      analyticsId,
      analyticsLabel,
      testID,
    })
    productTelemetryService.recordPress({
      elementId: identity.elementId,
      elementType: typeof accessibilityRole === 'string' ? accessibilityRole : 'pressable',
      metadata: {
        elementIdQuality: identity.elementIdQuality,
        elementLabel: identity.elementLabel,
      },
    })
    onPress?.(event)
  }

  return (
    <RNPressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole={accessibilityRole}
      onPress={handlePress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      style={typeof style === 'function' ? style(styleState) : style}
      testID={testID}
      {...props}
    />
  )
}
